// DEPLOY: /var/www/woody.wtf/frontend/src/chart/AnalystSummary.js
// Analyst ratings in the company strip (/chart/analyst: latest rating per firm). Shows the
// consensus as a Buy / Hold / Sell split bar, the target range (low, average, high) as a
// track with the current price marked and one tick per firm, and the upside to the average.
// Hover (desktop) or tap (phone) a firm's tick for its details. Rating dates can also be
// shown on the chart itself (View -> Analyst ratings on chart).
import React, { useState } from 'react';

const S = {
  wrap:   { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px', fontSize: '12px', paddingBottom: '2px' },
  label:  { fontSize: '10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.04em' },
  split:  { display: 'flex', width: '110px', height: '8px', borderRadius: '4px', overflow: 'hidden', background: 'var(--border)' },
  counts: { fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', whiteSpace: 'nowrap' },
  range:  { display: 'flex', alignItems: 'center', gap: '6px' },
  track:  { position: 'relative', width: '200px', height: '18px' },
  line:   { position: 'absolute', left: 0, right: 0, top: '8px', height: '2px', background: 'var(--border)', borderRadius: '1px' },
  end:    { fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  tip:    { position: 'fixed', zIndex: 3000, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 8px', boxShadow: 'var(--shadow)', fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.5, pointerEvents: 'none', minWidth: '150px' },
};
export const RATING_COLOUR = { Buy: 'var(--success)', Hold: 'var(--text-muted)', Sell: 'var(--error)' };
const money = (v, cur = '') => `${cur}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AnalystSummary({ data, price }) {
  const [tip, setTip] = useState(null);
  const ratings = data?.ratings || [];
  if (!ratings.length) return null;
  const { counts } = data, n = ratings.length, cur = ratings[0].currency || '';
  const targets = ratings.map(r => r.price_target);
  const lo = Math.min(...targets), hi = Math.max(...targets), avg = targets.reduce((a, b) => a + b, 0) / n;
  const min = Math.min(lo, price ?? lo), max = Math.max(hi, price ?? hi), span = max - min || 1;
  const pos = v => `${((v - min) / span) * 100}%`;
  const upside = price ? (avg / price - 1) * 100 : null;
  const show = (e, r) => setTip({ x: e.clientX, y: e.clientY, r });

  return (
    <div style={S.wrap}>
      <span style={S.label}>Analysts ({n})</span>
      <span style={S.split} title={`Buy ${counts.Buy} · Hold ${counts.Hold} · Sell ${counts.Sell}`}>
        {['Buy', 'Hold', 'Sell'].map(k => counts[k] > 0 && <span key={k} style={{ width: `${counts[k] / n * 100}%`, background: RATING_COLOUR[k] }} />)}
      </span>
      <span style={S.counts}>
        <span style={{ color: RATING_COLOUR.Buy }}>{counts.Buy} buy</span>{' · '}
        <span style={{ color: RATING_COLOUR.Hold }}>{counts.Hold} hold</span>{' · '}
        <span style={{ color: RATING_COLOUR.Sell }}>{counts.Sell} sell</span>
      </span>
      <span style={S.range}>
        <span style={S.end}>{money(lo, cur)}</span>
        <span style={S.track} onMouseLeave={() => setTip(null)}>
          <span style={S.line} />
          <span style={{ position: 'absolute', top: '4px', left: pos(lo), width: `calc(${pos(hi)} - ${pos(lo)})`, height: '10px', background: 'var(--accent-dim)', borderRadius: '5px' }} />
          {ratings.map((r, i) => (
            <span key={i} onMouseMove={e => show(e, r)} onClick={e => show(e, r)}
                  style={{ position: 'absolute', top: '3px', left: `calc(${pos(r.price_target)} - 1.5px)`, width: '3px', height: '12px', borderRadius: '1px', background: RATING_COLOUR[r.rating_norm] || 'var(--accent)', cursor: 'default' }} />
          ))}
          <span title={`Average target ${money(avg, cur)}`} style={{ position: 'absolute', top: '1px', left: `calc(${pos(avg)} - 1px)`, width: '2px', height: '16px', background: 'var(--accent)' }} />
          {price != null && (
            <span title={`Last price ${money(price, cur)}`}
                  style={{ position: 'absolute', top: '4px', left: `calc(${pos(price)} - 5px)`, width: '10px', height: '10px', borderRadius: '50%', background: 'var(--text)', border: '2px solid var(--surface)', boxSizing: 'border-box' }} />
          )}
        </span>
        <span style={S.end}>{money(hi, cur)}</span>
      </span>
      <span style={S.counts}>
        avg {money(avg, cur)}
        {upside != null && <b style={{ color: upside >= 0 ? RATING_COLOUR.Buy : RATING_COLOUR.Sell, marginLeft: '6px' }}>{upside >= 0 ? '+' : ''}{upside.toFixed(1)}%</b>}
      </span>
      {tip && (
        <div style={{ ...S.tip, left: Math.min(tip.x + 12, window.innerWidth - 190), top: tip.y + 14 }}>
          <div style={{ fontWeight: 700 }}>{tip.r.firm}</div>
          {tip.r.analyst && <div style={{ color: 'var(--text-muted)' }}>{tip.r.analyst}</div>}
          <div style={{ color: RATING_COLOUR[tip.r.rating_norm] }}>{tip.r.rating}{tip.r.action ? ` · ${tip.r.action}` : ''}</div>
          <div>Target {money(tip.r.price_target, cur)}</div>
          <div style={{ color: 'var(--text-muted)' }}>{tip.r.date}</div>
        </div>
      )}
    </div>
  );
}
